// Data of a "sci-excel" import (src/Service/Import/Handler/SciExcelImportHandler.php)

export type SciImportStatus = 'match' | 'no_match' | 'no_officer' | 'no_signal' | 'out_of_scope' | 'invalid_siren' | 'duplicate';
export type SciMatchLevel = 'sure' | 'probable' | 'none';
export type SciExistingState = 'none' | 'identical' | 'different';
export type SciImportOutcome = 'imported' | 'identical' | 'conflict_kept' | 'refused' | 'not_importable';
// "Client en gestion" column, written apart from the contact (no officer match needed)
export type SciManagedOutcome = 'updated' | 'unchanged';

export interface SciOfficer {
  key: string;
  lastname: string;
  firstnames: string[];
  role: string;
}

export interface SciContact {
  civilite: string | null;
  nom: string | null;
  prenom: string | null;
  telephone: string | null;
  email: string | null;
  adresse: string | null;
}

export interface SciFileContact {
  civilite: string;
  nom: string;
  prenom: string;
  telephone: string;
  email: string;
  adresse: string;
}

export interface SciAnalysisRow {
  line: number;
  siren: string;
  denomination: string;
  raw: Record<string, string>;
  file: SciFileContact;
  emails: string[];
  phones: string[];
  status: SciImportStatus;
  statusLabel: string | null;
  level: SciMatchLevel;
  reason: string;
  ambiguous: boolean;
  officers: SciOfficer[];
  proposedOfficerKey: string | null;
  proposedEmail: string | null;
  proposed: SciContact | null;
  existing: SciContact | null;
  existingState: SciExistingState;
  managed: boolean | null;
  existingManaged: boolean | null;
  managedChange: boolean;
}

export interface SciAnalysis {
  kind: 'sci-contacts';
  summary: Record<string, number>;
  headers: string[];
  rows: SciAnalysisRow[];
}

export interface SciReportRow {
  line: number;
  siren: string;
  denomination: string;
  raw: Record<string, string>;
  status: SciImportStatus;
  level: SciMatchLevel;
  reason: string;
  outcome: SciImportOutcome;
  motif: string | null;
  officer: SciOfficer | null;
  manual: boolean;
  contact: SciContact | null;
  officers: SciOfficer[];
  managed: boolean | null;
  managedOutcome: SciManagedOutcome | null;
}

export interface SciReport {
  kind: 'sci-contacts';
  applied: true;
  summary: Record<SciImportOutcome | 'manual' | 'managed', number>;
  headers: string[];
  rows: SciReportRow[];
}

export interface SciDecision {
  accepted: boolean;
  officerKey: string | null;
  email: string | null;
  replaceExisting: boolean;
}

export interface SciManualTreatment {
  status: 'in_progress' | 'completed';
  sciDecisions: Record<string, SciDecision>;
  lastUpdated?: string;
}

export const isSciReport = (data: unknown): data is SciReport => (
  typeof data === 'object' && data !== null && (data as { kind?: string }).kind === 'sci-contacts' && (data as { applied?: boolean }).applied === true
);

export const isSciAnalysis = (data: unknown): data is SciAnalysis => (
  typeof data === 'object' && data !== null && (data as { kind?: string }).kind === 'sci-contacts' && !(data as { applied?: boolean }).applied
);

export const formatOfficer = (officer: SciOfficer): string => `${officer.lastname} ${officer.firstnames.join(' ')}`.trim();

export const SCI_LEVEL_LABELS: Record<SciMatchLevel, string> = {
  sure: 'Sûr',
  probable: 'Probable',
  none: 'Aucun',
};

export const SCI_OUTCOME_LABELS: Record<SciImportOutcome, string> = {
  imported: 'Importé',
  identical: 'Déjà à jour',
  conflict_kept: 'Contact existant conservé',
  refused: 'Refusé en revue',
  not_importable: 'Non importable',
};
